import { useState, useCallback, useRef, useEffect, forwardRef, useImperativeHandle } from "react";
import { AutoSaveIndicator } from "newui/components/ui/AutoSave/AutoSaveIndicator";
import { useAutoSave } from "newui/hooks/useAutoSave";
import { ConsensusRow } from "./ConsensusRow";
import type { MeetingCriteriaScore, UpsertConsensusScoresRequest } from "../../types/meeting.type";

interface ConsensusPanelState {
  consensusScore: number | null;
  panelNotes: string | null;
  rfxInviteId: number;
}

export interface ConsensusPanelHandle {
  /** Immediately save any pending dirty scores (bypasses debounce). */
  flush: () => Promise<void>;
}

interface ConsensusPanelProps {
  data: MeetingCriteriaScore;
  /** Save callback receives the criteriaId the scores belong to, so the caller can pin it at save time. */
  onSave: (criteriaId: number, payload: UpsertConsensusScoresRequest) => Promise<void>;
  onActiveScoreChange?: (score: number | null) => void;
}

const buildInitialState = (data: MeetingCriteriaScore): ConsensusPanelState[] =>
  data.suppliers.map((s) => ({
    rfxInviteId: s.invite_id,
    consensusScore: s.consensus.score,
    panelNotes: s.consensus.panel_notes,
  }));

export const ConsensusPanel = forwardRef<ConsensusPanelHandle, ConsensusPanelProps>(({ data, onSave, onActiveScoreChange }, ref) => {
  const [rows, setRows] = useState<ConsensusPanelState[]>(() => buildInitialState(data));
  const [saveError, setSaveError] = useState<string | null>(null);
  const dirtyRef = useRef<Set<number>>(new Set());
  const changeTokenRef = useRef(0);
  const [changeToken, setChangeToken] = useState(0);

  // Pin the criterion ID so flushes always save against the correct criterion
  const criterionIdRef = useRef(data.criterion.id);

  // Re-initialize when criterion changes (data.criterion.id changes)
  const prevCriterionIdRef = useRef(data.criterion.id);
  if (data.criterion.id !== prevCriterionIdRef.current) {
    prevCriterionIdRef.current = data.criterion.id;
    criterionIdRef.current = data.criterion.id;
    setRows(buildInitialState(data));
    dirtyRef.current = new Set();
  }

  const isDirty = dirtyRef.current.size > 0;

  const handleRowChange = useCallback((updated: ConsensusPanelState) => {
    setRows((prev) =>
      prev.map((r) => (r.rfxInviteId === updated.rfxInviteId ? updated : r))
    );
    dirtyRef.current.add(updated.rfxInviteId);
    changeTokenRef.current += 1;
    setChangeToken(changeTokenRef.current);
  }, []);

  const saveDirty = useCallback(async (): Promise<false | void> => {
    const dirtyIds = Array.from(dirtyRef.current);
    if (dirtyIds.length === 0) {
      return;
    }
    setSaveError(null);
    const dirtyRows = rows.filter((r) => dirtyIds.includes(r.rfxInviteId));
    const payload: UpsertConsensusScoresRequest = {
      scores: dirtyRows.map((r) => ({
        rfx_invite_id: r.rfxInviteId,
        consensus_score: r.consensusScore,
        panel_notes: r.panelNotes,
      })),
    };
    try {
      await onSave(criterionIdRef.current, payload);
      dirtyRef.current = new Set();
    } catch {
      setSaveError("Failed to save — changes will retry");
      return false;
    }
  }, [rows, onSave]);

  const { isSaving, lastSavedAt, flushSave } = useAutoSave({
    isDirty,
    onSave: saveDirty,
    changeToken,
    debounceMs: 2500,
  });

  useImperativeHandle(ref, () => ({
    flush: flushSave,
  }), [flushSave]);

  // Reset error when dirty state clears
  useEffect(() => {
    if (!isDirty) {
      setSaveError(null);
    }
  }, [isDirty]);

  return (
    <div className="border-t-2 border-gray-200 bg-white px-4 py-3 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.07)]">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
          Consensus Scores
        </h3>
        <AutoSaveIndicator
          isSaving={isSaving}
          isPending={isDirty && !isSaving}
          lastSavedAt={lastSavedAt}
          error={saveError}
          size="xs"
        />
      </div>

      <div data-testid="consensus-panel-scroll">
        {rows.map((row) => {
          const supplier = data.suppliers.find((s) => s.invite_id === row.rfxInviteId);
          return (
            <ConsensusRow
              key={row.rfxInviteId}
              supplierName={supplier?.supplier_name ?? String(row.rfxInviteId)}
              state={row}
              average={supplier?.stats.average ?? null}
              onChange={handleRowChange}
              onActiveScoreChange={onActiveScoreChange}
            />
          );
        })}
      </div>
    </div>
  );
});

ConsensusPanel.displayName = "ConsensusPanel";
